import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import authService from '../services/authService';

const EmailVerification: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isVerifying, setIsVerifying] = useState(true);

  const token = searchParams.get('token');

  useEffect(() => {
    if (token) {
      verifyEmail();
    } else {
      setIsVerifying(false);
      setError('Invalid verification link. Please check your email for the correct link.');
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
      setLoading(true);
      setError(''); // Clear any previous errors
      setSuccess(''); // Clear any previous success messages
      await authService.verifyEmail(token!);
      setSuccess('Email verified successfully! You can now log in to your account.');
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      setSuccess(''); // Clear success message on error
      if (err.message.includes('Invalid or expired')) {
        setError('This verification link is invalid or has expired. Please request a new verification email.');
      } else {
        setError(err.message || 'Email verification failed. Please try again.');
      }
    } finally {
      setLoading(false);
      setIsVerifying(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Get email from localStorage or prompt user
      const user = authService.getCurrentUser();
      const email = user?.email || prompt('Please enter your email address:');
      
      if (!email) {
        setError('Email is required');
        return;
      }

      // TODO: Implement resend verification functionality
      // await authService.resendVerification(email);
      setSuccess('Verification email functionality will be implemented soon.');
    } catch (err: any) {
      setError(err.message || 'Failed to resend verification email.');
    } finally {
      setLoading(false);
    }
  };

  if (isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Verifying Email</h2>
            <p className="text-gray-600">Please wait while we verify your email address...</p>
            {loading && (
              <div className="mt-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#5b3cc4] mx-auto"></div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Email Verification</h2>
          
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
              {error}
            </div>
          )}
          
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
              {success}
            </div>
          )}
          
          {!success && (
            <div className="space-y-4">
              <p className="text-gray-600">
                {error 
                  ? "We couldn't verify your email address. This could be because:"
                  : "Please verify your email address to complete your registration."
                }
              </p>
              
              {error && (
                <ul className="text-sm text-gray-600 text-left list-disc list-inside space-y-1">
                  <li>The verification link has expired</li>
                  <li>The verification link is invalid</li>
                  <li>You've already verified your email</li>
                </ul>
              )}
              
              <div className="space-y-3">
                <button
                  onClick={handleResendVerification}
                  disabled={loading}
                  className="w-full flex justify-center py-3 px-4 border border-transparent text-base font-semibold text-white focus:outline-none focus:ring-2 focus:ring-[#5b3cc4] focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    borderRadius: '12px',
                    background: 'linear-gradient(90deg, #362E7F 0%, #5A4ABE 45.67%, #7F60BF 100%)'
                  }}
                >
                  {loading ? 'Sending...' : 'Resend Verification Email'}
                </button>
                
                <button
                  onClick={() => navigate('/login')}
                  className="w-full flex justify-center py-3 px-4 border border-gray-300 text-base font-semibold text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#5b3cc4] focus:ring-offset-2 transition-colors"
                  style={{ borderRadius: '12px' }}
                >
                  Back to Login
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailVerification; 